import React from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import styles from './SMLButton.module.scss';

export type SMLButtonVariant = 'default' | 'primary' | 'follow' | 'following';

export interface SMLButtonProps {
  variant?: SMLButtonVariant;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  title?: string;
  ariaLabel?: string;
  /** A button that opens and closes a region: is the region open now? */
  ariaExpanded?: boolean;
  /** The id of the region that the button opens and closes. */
  ariaControls?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Draws a link with the button look, which opens this address in a new tab. */
  href?: string;
}

function variantClass(variant: SMLButtonVariant): string | undefined {
  switch (variant) {
    case 'primary':
      return styles.primary;
    case 'follow':
      return styles.follow;
    case 'following':
      return styles.following;
    case 'default':
      return undefined;
    default:
      return undefined;
  }
}

const SMLButtonComponent: React.FC<SMLButtonProps> = ({
  variant = 'default',
  icon,
  children,
  className,
  type = 'button',
  disabled,
  title,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  onClick,
  href,
}) =>
  href !== undefined ? (
    <a
      className={clsx(styles.btn, variantClass(variant), className)}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      aria-label={ariaLabel}
    >
      {icon}
      {children}
    </a>
  ) : (
    <button
      className={clsx(styles.btn, variantClass(variant), className)}
      // eslint-disable-next-line react/button-has-type
      type={type}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      onClick={onClick}
    >
      {icon}
      {children}
    </button>
  );

export const SMLButton = TypedMemo(SMLButtonComponent);
